import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { onboardingApi } from "@/api/onboarding-api";
import { LoadingSpinner } from "../ui/loading-spinner";

/** Query key shared with the chat so completing onboarding invalidates the gate. */
export const ONBOARDING_STATE_KEY = ["onboarding", "state"] as const;

/**
 * Sends signed-in users who haven't finished onboarding into the chat.
 *
 * Wraps the app's protected routes rather than living inside them, so the
 * redirect happens before any workspace-scoped page tries to load data the
 * user doesn't have yet.
 *
 * Failing open is deliberate: if `/onboarding/state` errors, an established
 * user must still reach their dashboard. The cost of a missed redirect is a
 * user who skips onboarding; the cost of failing closed is a lockout.
 */
export function OnboardingGate() {
  const { isSignedIn, isLoaded } = useUser();
  const location = useLocation();

  const { data, isLoading, isError } = useQuery({
    queryKey: ONBOARDING_STATE_KEY,
    queryFn: onboardingApi.getState,
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
    staleTime: 30_000,
  });

  // Never bounce a user who is already in the chat — that's a redirect loop.
  const isOnOnboarding = location.pathname === "/onboarding";

  if (!isLoaded || (isSignedIn && isLoading && !isOnOnboarding)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError || !data) {
    return <Outlet />;
  }

  const { status } = data.onboarding;
  const needsOnboarding = status === "not_started" || status === "in_progress";

  if (needsOnboarding && !isOnOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Finished users have no business back in the chat.
  if (!needsOnboarding && isOnOnboarding) {
    const workspaceId = data.onboarding.workspace;
    return (
      <Navigate to={workspaceId ? `/${workspaceId}/dashboard` : "/workspaces"} replace />
    );
  }

  return <Outlet />;
}
